import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { TopStrip } from './components/layout/TopStrip.js';
import { Header } from './components/layout/Header.js';
import { BottomNav } from './components/layout/BottomNav.js';
import { OverviewPage } from './components/dashboard/OverviewPage.js';
import { RatesPage } from './components/rates/RatesPage.js';
import { SpotPage } from './components/spot/SpotPage.js';
import { EarningsPage } from './components/earnings/EarningsPage.js';
import { PaymentsPage } from './components/earnings/PaymentsPage.js';
import { SettingsPage } from './components/settings/SettingsPage.js';
import { ToastProvider, useToast } from './components/shared/Toast.js';
import { useFarms } from './hooks/useFarms.js';
import { usePool } from './hooks/usePool.js';
import { useSettings } from './hooks/useSettings.js';
import { useAutoRefresh } from './hooks/useAutoRefresh.js';
import { AiChat } from './components/shared/AiChat.js';
import { LoginScreen, getSessionToken } from './components/auth/LoginScreen.js';
import { DetoxLock, DetoxOpenBanner } from './components/shared/DetoxLock.js';
import { useDetox } from './utils/detox.js';
import { REFRESH_INTERVAL, type SectionId } from './utils/constants.js';

function readHashSection(): SectionId {
  const hash = window.location.hash.slice(1);
  const valid: SectionId[] = ['overview', 'rates', 'spot', 'earnings', 'payments', 'settings'];
  return valid.includes(hash as SectionId) ? (hash as SectionId) : 'overview';
}

function AppInner() {
  const [section, setSectionState] = useState<SectionId>(readHashSection);

  const setSection = useCallback((s: SectionId) => {
    setSectionState(s);
    window.location.hash = s;
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const handler = () => setSectionState(readHashSection());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  const { show: toast } = useToast();

  const settings = useSettings();
  const { farms, loading: farmsLoading, refresh: refreshFarms } = useFarms(settings.offlineThreshold);
  const { pool, loading: poolLoading, refresh: refreshPool } = usePool();

  // Auto-refresh
  useAutoRefresh(async () => {
    await Promise.all([refreshFarms(), refreshPool()]);
  }, REFRESH_INTERVAL);

  // Dynamic document title
  const onlineFarms = farms.filter((f) => f.isOnline).length;
  const offlineFarms = farms.filter((f) => !f.isOnline).length;
  useEffect(() => {
    const parts = [];
    if (onlineFarms > 0) parts.push(`${onlineFarms} online`);
    if (offlineFarms > 0) parts.push(`${offlineFarms} offline`);
    document.title = parts.length > 0
      ? `${parts.join(', ')} | CRYPTODASH`
      : 'CRYPTODASH — Mining Portal';
  }, [onlineFarms, offlineFarms]);

  // Favicon badge for offline farms
  useEffect(() => {
    if (offlineFarms > 0) {
      updateFaviconBadge(offlineFarms);
    } else {
      resetFavicon();
    }
  }, [offlineFarms]);

  // Toast notifications for farm status
  const prevFarmStatus = useRef<Map<number, boolean>>(new Map());
  useEffect(() => {
    if (farmsLoading || farms.length === 0) return;
    const prev = prevFarmStatus.current;
    const offline = farms.filter((f) => !f.isOnline);

    // Notify about farms that just came back online
    for (const farm of farms) {
      const wasOnline = prev.get(farm.id);
      if (wasOnline === false && farm.isOnline) {
        toast(`${farm.name} снова онлайн`, 'success');
      }
    }

    // Always show offline banner if any farms are down
    if (offline.length > 0) {
      toast(`${offline.map((f) => f.name).join(', ')} — офлайн`, 'error');
    }

    prevFarmStatus.current = new Map(farms.map((f) => [f.id, f.isOnline]));
  }, [farms, farmsLoading, toast]);

  const handleTimeoutChange = (idx: number) => {
    settings.setTimeoutByIndex(idx);
    toast('Таймаут оффлайн обновлён', 'info');
  };

  const totalPower = farms.filter((f) => f.isOnline).reduce((s, f) => s + f.power, 0);

  // Детокс: Курсы/Спот/Доход открыты только по расписанию (МСК)
  const detox = useDetox();

  return (
    <div class="app">
      <TopStrip
        theme={settings.theme}
        onThemeToggle={settings.toggleTheme}
        timeoutIndex={settings.timeoutIndex}
        onTimeoutChange={handleTimeoutChange}
        hideTicker={!detox.open}
      />

      <main class="main-content">
        <Header
          section={section}
          timeoutIndex={settings.timeoutIndex}
          onTimeoutChange={handleTimeoutChange}
          theme={settings.theme}
          onThemeToggle={settings.toggleTheme}
          onMenuToggle={() => {}}
        />

        <div class={`content-section${section === 'overview' ? ' active' : ''}`}>
          {section === 'overview' && (
            <OverviewPage
              farms={farms}
              pool={pool}
              loading={farmsLoading}
              coreTempThreshold={settings.coreTempThreshold}
              memoryTempThreshold={settings.memoryTempThreshold}
              totalPower={totalPower}
              electricityRate={settings.electricityRate}
              usdRub={settings.usdRub}
            />
          )}
        </div>

        <div class={`content-section${section === 'rates' ? ' active' : ''}`}>
          {section === 'rates' && (detox.open ? <><DetoxOpenBanner /><RatesPage /></> : <DetoxLock sectionName="Курсы" />)}
        </div>

        <div class={`content-section${section === 'spot' ? ' active' : ''}`}>
          {section === 'spot' && (detox.open ? <><DetoxOpenBanner /><SpotPage /></> : <DetoxLock sectionName="Спот" />)}
        </div>

        <div class={`content-section${section === 'earnings' ? ' active' : ''}`}>
          {section === 'earnings' && (detox.open ? <><DetoxOpenBanner /><EarningsPage pool={pool} usdRub={settings.usdRub} /></> : <DetoxLock sectionName="Доход" />)}
        </div>

        <div class={`content-section${section === 'payments' ? ' active' : ''}`}>
          {section === 'payments' && (detox.open ? <><DetoxOpenBanner /><PaymentsPage pool={pool} /></> : <DetoxLock sectionName="Выплаты" />)}
        </div>

        <div class={`content-section${section === 'settings' ? ' active' : ''}`}>
          {section === 'settings' && (
            <SettingsPage
              coreTempThreshold={settings.coreTempThreshold}
              memoryTempThreshold={settings.memoryTempThreshold}
              onCoreTempChange={settings.setCoreTemp}
              onMemoryTempChange={settings.setMemoryTemp}
              electricityRate={settings.electricityRate}
              onElectricityRateChange={settings.setElectricityRate}
              theme={settings.theme}
              onThemeChange={settings.setTheme}
            />
          )}
        </div>
      </main>

      <BottomNav currentSection={section} onNavigate={setSection} />
      <AiChat />
    </div>
  );
}

export function App() {
  const [authenticated, setAuthenticated] = useState(() => !!getSessionToken());

  if (!authenticated) {
    return <LoginScreen onAuthenticated={() => setAuthenticated(true)} />;
  }

  return (
    <ToastProvider>
      <AppInner />
    </ToastProvider>
  );
}

// ── Favicon badge ────────────────────────────────────────────
let originalFavicon: string | null = null;

function updateFaviconBadge(count: number) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  if (!originalFavicon) originalFavicon = link.href;

  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    ctx.drawImage(img, 0, 0, 64, 64);
    // Red badge circle
    ctx.fillStyle = '#F5365C';
    ctx.beginPath();
    ctx.arc(50, 14, 14, 0, 2 * Math.PI);
    ctx.fill();
    // Badge text
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(count), 50, 15);
    link.href = canvas.toDataURL('image/png');
  };
  img.src = originalFavicon;
}

function resetFavicon() {
  if (!originalFavicon) return;
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = originalFavicon;
}
